import React from 'react';
import { EditorState, TextNode } from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useController, useFormContext } from 'react-hook-form';

import { AutoLinkNode, LinkNode } from '@lexical/link';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { TableNode, TableCellNode, TableRowNode } from '@lexical/table';
import { ListNode, ListItemNode } from '@lexical/list';
import { Box } from '@mui/material';
import LexicalEditorTopBar from './components/rich-text-topbar';

import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { LoadInitialContent } from './components/load-initial';
import { ExtendedTextNode } from './nodes/extended-text-node';
import { OnChangePlugin } from './plugins/on-change-plugin';

export interface RichTextEditorProps {
    name: string;
    showErrors?: boolean;
}

export interface onChangePluginProps {
    onChange: (data: { content: string; editorState: EditorState }) => void;
}

const theme = {
    // Theme styling goes here
    //...
};

function onError(error: any) {
    console.error(error);
}

export const lexicalEditorConfig = {
    namespace: 'MyEditor',
    theme: theme,
    onError,

    nodes: [
        ExtendedTextNode,
        {
            replace: TextNode,
            with: (node: TextNode) => new ExtendedTextNode(node.__text),
            withKlass: ExtendedTextNode,
        },
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        CodeNode,
        CodeHighlightNode,
        TableNode,
        TableCellNode,
        TableRowNode,
        AutoLinkNode,
        LinkNode,
    ],
};

export const RichTextEditor: React.FC<RichTextEditorProps> = props => {
    const { name } = props;

    const { control } = useFormContext();
    const { field } = useController({
        control,
        name,
    });

    return (
        <Box>
            <LexicalComposer initialConfig={lexicalEditorConfig}>
                <LexicalEditorTopBar />

                <Box
                    sx={{
                        border: '1px solid #DFE1ED',
                        borderRadius: '32px',
                        pb: 2,
                    }}
                >
                    <LoadInitialContent initialContent={field.value?.content} />
                    <RichTextPlugin
                        contentEditable={
                            <Box
                                component={ContentEditable}
                                sx={{
                                    my: 1,
                                    mx: 3,
                                    minHeight: '120px',
                                    '&:focus': {
                                        outline: 'none',
                                    },
                                }}
                            />
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <OnChangePlugin onChange={field.onChange} />
                    <ListPlugin />
                    <LinkPlugin />
                </Box>
            </LexicalComposer>
        </Box>
    );
};
